import { useQuery } from "@tanstack/react-query";

import axios from "./axios";

/**
 * La politique de mot de passe, telle que le SERVEUR l'applique.
 *
 * ── POURQUOI CE FICHIER ─────────────────────────────────────────────────────
 *
 * La regle etait ecrite trois fois, differemment :
 *
 *   Invitation.tsx      8 caracteres + minuscule + majuscule + chiffre + symbole
 *   ForgotPassword.tsx  rien du tout, hormis l'egalite des deux champs
 *   l'API               `min:8` sur un chemin, aucune regle sur l'autre
 *
 * Consequence : selon l'ecran par lequel on passait, le meme compte pouvait
 * recevoir un mot de passe d'un caractere ou se voir refuser un mot de passe
 * parfaitement raisonnable. Et le controle le plus strict etait celui qui ne
 * protege rien, puisqu'il s'applique dans un navigateur.
 *
 * La politique vit maintenant dans config/security.php cote API et se lit ici.
 * `describePolicy` sert a afficher les exigences AVANT la saisie, plutot que de
 * les reveler par un refus apres coup.
 */

export type TPasswordPolicy = {
  min_length: number;
  max_length: number;
  require_lowercase: boolean;
  require_uppercase: boolean;
  require_digit: boolean;
  require_symbol: boolean;
};

/**
 * Repli utilise tant que la reponse n'est pas arrivee, ou si l'API est plus
 * ancienne que cette route. Volontairement identique a la valeur par defaut du
 * serveur : un repli plus permissif ferait passer la validation cote client
 * pour rien, un repli plus strict refuserait des mots de passe que le serveur
 * accepte.
 */
export const DEFAULT_PASSWORD_POLICY: TPasswordPolicy = {
  min_length: 8,
  max_length: 72,
  require_lowercase: true,
  require_uppercase: true,
  require_digit: true,
  require_symbol: true,
};

export const usePasswordPolicy = () => {
  const { data } = useQuery({
    queryKey: ["auth", "password-policy"],
    queryFn: async (): Promise<TPasswordPolicy> => {
      const { data } = await axios.get("/auth/password-policy");
      return data;
    },
    // La politique ne bouge qu'au deploiement : inutile de la redemander a
    // chaque montage d'ecran.
    staleTime: 60 * 60 * 1000,
    retry: false,
  });

  return data ?? DEFAULT_PASSWORD_POLICY;
};

/**
 * Le mot de passe respecte-t-il la politique ?
 *
 * Meme classe de symboles que le serveur (`[^\w\s]`, c'est-a-dire tout ce qui
 * n'est ni lettre, ni chiffre, ni espace) plutot qu'une liste enumeree : une
 * liste exclut les symboles des claviers non francais, et faire diverger les
 * deux cotes ramenerait exactement le probleme que ce fichier supprime.
 */
export const validatePassword = (
  value: string,
  policy: TPasswordPolicy = DEFAULT_PASSWORD_POLICY
): boolean => {
  if (value.length < policy.min_length) return false;
  if (value.length > policy.max_length) return false;
  if (policy.require_lowercase && !/[a-z]/.test(value)) return false;
  if (policy.require_uppercase && !/[A-Z]/.test(value)) return false;
  if (policy.require_digit && !/\d/.test(value)) return false;
  if (policy.require_symbol && !/[^\w\s]/.test(value)) return false;
  return true;
};

/**
 * Les exigences en une phrase, pour les afficher avant la saisie.
 *
 * `t` est passe en argument plutot qu'appele via un hook : cette fonction sert
 * aussi bien dans un composant que dans un gestionnaire d'evenement.
 */
export const describePolicy = (
  t: (key: string, fallback: string, options?: any) => string,
  policy: TPasswordPolicy = DEFAULT_PASSWORD_POLICY
): string => {
  const parts: string[] = [];
  if (policy.require_lowercase) parts.push(t("password.lowercase", "une minuscule"));
  if (policy.require_uppercase) parts.push(t("password.uppercase", "une majuscule"));
  if (policy.require_digit) parts.push(t("password.digit", "un chiffre"));
  if (policy.require_symbol) parts.push(t("password.symbol", "un caractère spécial"));

  const base = t("password.minLength", "Au moins {{count}} caractères", {
    count: policy.min_length,
  });

  return parts.length > 0 ? `${base}, ${t("password.including", "dont")} ${parts.join(", ")}.` : `${base}.`;
};
